import React, { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../service/Api";
import ViewCompanyDashboard, {
  CompanyDetails,
  Representative,
  Branch,
  CompanyNote,
  LinkedContact,
  CompanyDocument,
  CompanyBooking,
} from "./ViewCompany2";
import AddRepresentativeDrawer from "./add-company2/AddRepresentativesDrawer";
import AddDocumentDrawer from "./add-company2/AddDocumentDrawer";
import AddBranchDrawer from "./add-company2/AddBranchDrawer";
import { AxiosResponse } from "axios";

// ── API response types ───────────────────────────────────────────────────

interface NoteResponse {
  id: number;
  company: number;
  note: string;
  created_by: {
    first_name: string;
    last_name: string;
    email: string;
    profile_picture: string;
  };
  created_at: string;
  updated_at: string;
}

interface BranchResponse {
  id: number;
  name: string;
  city?: string;
  country?: string;
  phone_number?: string;
  email?: string;
  street_address?: string;
}

interface ContactResponse {
  id: number;
  full_name: string;
  email: string;
  role: string | null;
  phone: string;
  contact_image: string | null;
  branch_name: string;
}

// ── Transformers ─────────────────────────────────────────────────────────

function transformNote(n: NoteResponse): CompanyNote {
  return {
    id: n.id,
    author_name: `${n.created_by.first_name} ${n.created_by.last_name}`,
    author_avatar: n.created_by.profile_picture,
    created_at: n.created_at,
    content: n.note,
  };
}

function transformBranch(
  b: BranchResponse,
  companyName: string,
  representatives: Representative[],
  bookings: CompanyBooking[],
): Branch {
  const branchReps = representatives.filter((r) => r.branch_name === b.name);
  const manager =
    branchReps.find((r) => (r.role || "").toLowerCase().includes("manager")) ??
    branchReps[0];

  const bookingsCount = bookings.filter(
    (bk) => bk.branch === `${companyName} - ${b.name}` || bk.branch === b.name,
  ).length;

  return {
    id: b.id,
    name: b.name,
    city: b.city,
    country: b.country,
    contacts_count: branchReps.length,
    bookings_count: bookingsCount,
    manager: manager
      ? {
          name:
            manager.first_name && manager.last_name
              ? `${manager.first_name} ${manager.last_name}`
              : manager.name,
          avatar: manager.avatar ?? undefined,
        }
      : undefined,
  };
}

function transformContact(c: ContactResponse): LinkedContact {
  const nameParts = (c.full_name || "").split(" ");
  return {
    id: c.id,
    first_name: nameParts[0] || "",
    last_name: nameParts.slice(1).join(" "),
    avatar: c.contact_image,
  };
}

function transformContactToRep(c: ContactResponse): Representative {
  const nameParts = (c.full_name || "").split(" ");
  const first_name = nameParts[0] || "";
  const last_name = nameParts.slice(1).join(" ");
  return {
    id: c.id,
    first_name,
    last_name,
    role: c.role ?? undefined,
    email: c.email,
    phone: c.phone,
    avatar: c.contact_image,
    branch_name: c.branch_name, // ← show which branch they belong to
  };
}
// ── Component ────────────────────────────────────────────────────────────

const ViewCompany2: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<CompanyBooking[]>([]);
  const [companyData, setCompanyData] = useState<CompanyDetails | null>(null);
  const [representatives, setRepresentatives] = useState<Representative[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [notes, setNotes] = useState<CompanyNote[]>([]);
  const [repDrawerOpen, setRepDrawerOpen] = useState(false);
  const [branchDrawerOpen, setBranchDrawerOpen] = useState(false);
  const [docDrawerOpen, setDocDrawerOpen] = useState(false);
  const [linkedContacts, setLinkedContacts] = useState<LinkedContact[]>([]);

  const [loading, setLoading] = useState(true);

  const [documents, setDocuments] = useState<CompanyDocument[]>([]);
  // ── Fetch all data ─────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const companyRes = await apiService.get<CompanyDetails>(
        `api/companies/company/${id}/`,
      );
      setCompanyData(companyRes.data);

      const branchRes = await apiService.get<{ results: BranchResponse[] }>(
        `api/companies/company-branch/?company=${id}`,
      );
      const rawBranches = branchRes.data.results ?? [];

      const contactRes = await apiService.get<{ results: ContactResponse[] }>(
        `api/contacts/contact/by-company/?company_id=${id}`,
      );
      const allContacts = contactRes.data.results ?? [];
      const reps = allContacts.map(transformContactToRep);
      setRepresentatives(reps);
      setLinkedContacts(allContacts.map(transformContact));

      try {
        const docsRes = await apiService.get<{
          results: {
            id: number;
            name: string;
            document: string;
            created_at: string;
          }[];
        }>(`api/companies/company-documents/?company=${id}`);
        setDocuments(
          (docsRes.data.results ?? []).map((d) => ({
            id: d.id,
            name: d.name || "Document",
            url: d.document,
            date: new Date(d.created_at).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }),
          })),
        );
      } catch (err) {
        console.error("Failed to fetch company documents:", err);
      }

      const bookingsData = await fetchAllBookings(id);
      setBookings(bookingsData);

      setBranches(
        rawBranches.map((b) =>
          transformBranch(b, companyRes.data.name, reps, bookingsData),
        ),
      );

      const notesRes = await apiService.get<{ results: NoteResponse[] }>(
        `api/companies/company-notes/?company=${id}`,
      );
      setNotes((notesRes.data.results ?? []).map(transformNote));
    } catch (err) {
      console.error("Failed to fetch company data:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  async function fetchAllBookings(id: string): Promise<CompanyBooking[]> {
    interface BookingsPage {
      results: CompanyBooking[];
      next: string | null;
    }

    let url: string | null =
      `api/companies/company/${id}/bookings/?page_size=100`;
    const all: CompanyBooking[] = [];

    while (url) {
      const pageRes: AxiosResponse<BookingsPage> =
        await apiService.get<BookingsPage>(url);
      all.push(...(pageRes.data.results ?? []));
      url = pageRes.data.next;
    }

    return all;
  }
  // ── Add note ───────────────────────────────────────────────────────────
  const handleAddNote = async (content: string) => {
    if (!id) return;
    try {
      const res = await apiService.post<NoteResponse>(
        `api/companies/company-notes/`,
        {
          company: Number(id),
          note: content,
        },
      );
      setNotes((prev) => [transformNote(res.data), ...prev]);
    } catch (err) {
      console.error("Failed to add note:", err);
    }
  };

  // ── Health score ───────────────────────────────────────────────────────
  const computeHealthScore = (company: CompanyDetails): number => {
    const fields: (keyof CompanyDetails)[] = [
      "name",
      "company_type",
      "description",
      "phone_number",
      "primary_email",
      "website",
      "street_address",
      "city",
      "country",
    ];
    const filled = fields.filter((f) => !!company[f]).length;
    return Math.round((filled / fields.length) * 100);
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!companyData) return null;

  const hqBranch = branches.find((b) => b.name === "HQ");
  return (
    <>
      <ViewCompanyDashboard
        companyData={companyData}
        healthScore={computeHealthScore(companyData)}
        representatives={representatives}
        branches={branches}
        bookings={bookings}
        notes={notes}
        linkedContacts={linkedContacts}
        linkedContactsTotal={linkedContacts.length}
        documents={documents}
        quickActions={[
          {
            label: "Add Representative",
            onClick: () => setRepDrawerOpen(true),
          },
          ...(companyData.company_type === "funeral_home"
            ? [
                {
                  label: "Add Booking",
                  onClick: () =>
                    navigate("/bookings/add/FUNERAL", {
                      state: {
                        serviceType: "FUNERAL",
                        prefillData: {
                          client_type: "funeral_home",
                          booking_type: "FUNERAL_HOME",
                          funeral_home_id: companyData.id,
                          company_name: companyData.name,
                          funeral_home_locked: true,
                        },
                      },
                    }),
                },
              ]
            : []),
          { label: "Add Branch", onClick: () => setBranchDrawerOpen(true) },
          { label: "Upload Document", onClick: () => setDocDrawerOpen(true) },
        ]}
        onEditClick={() => navigate(`/companies/${id}/edit`)}
        onAddRepresentative={() => setRepDrawerOpen(true)}
        onAddBranch={() => setBranchDrawerOpen(true)}
        onAddDocument={() => setDocDrawerOpen(true)}
        onAddNote={handleAddNote}
        onBranchClick={(branch) => {
          console.log("Branch clicked:", branch);
        }}
        formatDate={formatDate}
      />

      <AddBranchDrawer
        open={branchDrawerOpen}
        onClose={() => setBranchDrawerOpen(false)}
        companyId={companyData.id}
        onSuccess={fetchAll}
      />

      <AddRepresentativeDrawer
        open={repDrawerOpen}
        onClose={() => setRepDrawerOpen(false)}
        companyId={companyData.id}
        hqBranchId={hqBranch?.id ?? 0}
        onSuccess={fetchAll}
      />

      <AddDocumentDrawer
        open={docDrawerOpen}
        onClose={() => setDocDrawerOpen(false)}
        companyId={companyData.id}
        onSuccess={fetchAll}
      />
    </>
  );
};

export default ViewCompany2;
